import { useState, useEffect } from 'react';
import { useAuth } from './useAuth';
import { AdminService } from '@/core/services/AdminService';

interface UseIsAdminReturn {
  isAdmin: boolean;
  isModerator: boolean;
  loading: boolean;
}

/**
 * Hook to check if the current user is an admin or moderator
 * Uses AdminService with caching for performance
 */
export const useIsAdmin = (): UseIsAdminReturn => {
  const { user } = useAuth();
  const [isAdmin, setIsAdmin] = useState(false);
  const [isModerator, setIsModerator] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user?.id) {
      setIsAdmin(false);
      setIsModerator(false);
      setLoading(false);
      return;
    }

    const checkRoles = async () => {
      try {
        const [adminResult, moderatorResult] = await Promise.all([
          AdminService.isAdmin(user.id),
          AdminService.isModerator(user.id)
        ]);
        
        setIsAdmin(adminResult);
        setIsModerator(moderatorResult);
      } catch (error) {
        console.error('Error checking admin roles:', error);
        setIsAdmin(false);
        setIsModerator(false);
      } finally {
        setLoading(false);
      }
    };

    checkRoles();
  }, [user?.id]);

  return { isAdmin, isModerator, loading };
};
